"use client";

import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  categoryNameFromTarget,
  markActiveFragmentCategory,
} from "./fragmentCategories";

const EXPLICIT_SELECTION_LOCK_MS = 1200;

function categoryFromHash(hash: string): string | undefined {
  try {
    return decodeURIComponent(hash.slice(1)) || undefined;
  } catch {
    return undefined;
  }
}

export function useFragmentActiveCategory(
  containerRef: RefObject<HTMLElement | null>,
  categoryInView: string | null | undefined,
  html: string | null | undefined
): void {
  const [activeName, setActiveName] = useState<string>();
  const latestCategoryInView = useRef(categoryInView);
  const explicitSelectionTimer = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const selectExplicitly = useCallback((name: string) => {
    if (explicitSelectionTimer.current) {
      clearTimeout(explicitSelectionTimer.current);
    }
    setActiveName(name);
    explicitSelectionTimer.current = setTimeout(() => {
      explicitSelectionTimer.current = undefined;
      setActiveName(latestCategoryInView.current ?? undefined);
    }, EXPLICIT_SELECTION_LOCK_MS);
  }, []);

  useEffect(() => {
    latestCategoryInView.current = categoryInView;
    if (!categoryInView || explicitSelectionTimer.current) return;
    setActiveName(categoryInView);
  }, [categoryInView]);

  useEffect(() => {
    const selectFromHash = () => {
      const name = categoryFromHash(globalThis.location.hash);
      if (name) selectExplicitly(name);
    };
    globalThis.addEventListener("hashchange", selectFromHash);
    return () => globalThis.removeEventListener("hashchange", selectFromHash);
  }, [selectExplicitly]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const selectFromClick = (event: MouseEvent) => {
      const name = categoryNameFromTarget(root, event.target);
      if (name) selectExplicitly(name);
    };
    root.addEventListener("click", selectFromClick);
    return () => root.removeEventListener("click", selectFromClick);
  }, [containerRef, html, selectExplicitly]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !activeName) return;
    markActiveFragmentCategory(root, activeName)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeName, containerRef, html]);

  useEffect(
    () => () => {
      if (explicitSelectionTimer.current) {
        clearTimeout(explicitSelectionTimer.current);
      }
    },
    []
  );
}
